import React, {
  FC,
  forwardRef,
  ForwardRefRenderFunction,
  InputHTMLAttributes,
  useId,
  useState,
} from 'react'

import { FormLabelProps } from '../../FormLabel'

import * as SC from './styled'

export type FormFieldTextAreaProps =
  InputHTMLAttributes<HTMLTextAreaElement> & {
    name?: string
    className?: string
    error?: string
    hasError?: boolean
    helpText?: string
    errors?: { message?: string }[]
    label?: string
    text?: string
    labelProps?: FormLabelProps
    required?: boolean
    leftAdornment?: React.ReactElement
    rightAdornment?: React.ReactElement
    InputComponent?: FC<any>
    inputProps?: any
    hasFloatingLabel?: boolean
    size?: 'default' | 'small'
  }

const FormFieldTextAreaRenderer: ForwardRefRenderFunction<
  any,
  FormFieldTextAreaProps
> = (
  {
    className,
    name,
    id,
    error,
    hasError,
    text,
    placeholder,
    onChange,
    label,
    labelProps,
    required,
    helpText,
    leftAdornment,
    rightAdornment,
    InputComponent,
    inputProps,
    value,
    hasFloatingLabel,
    size = 'default',
    ...extraProps
  },
  ref
) => {
  const localId = useId()
  const [isFocused, setFocused] = useState(false)
  const isActive = isFocused || !!value
  const hasLabel = !!label

  const CustomInput: FC<any> = InputComponent ? InputComponent : SC.Input

  return (
    <SC.Container className={className}>
      <SC.Wrapper>
        {!hasFloatingLabel && (
          <SC.Label
            required={required}
            $active={isActive}
            $hasFloatingLabel={hasFloatingLabel}
            htmlFor={id ?? localId}
            {...labelProps}
          >
            {label}
          </SC.Label>
        )}
        {text && <SC.Text>{text}</SC.Text>}
        <SC.InputContainer
          $isError={!!error || hasError}
          $isActive={isActive}
          $isDisabled={extraProps.disabled}
          $hasLabel={hasLabel}
        >
          {hasFloatingLabel && (
            <SC.Label
              required={required}
              $active={isActive}
              $hasFloatingLabel={hasFloatingLabel}
            >
              {label}
            </SC.Label>
          )}
          <SC.InputWrapper>
            {leftAdornment}
            <CustomInput
              ref={ref}
              {...inputProps}
              {...extraProps}
              id={id ?? localId}
              name={name}
              type={extraProps.type ?? 'text'}
              placeholder={
                !hasLabel || !hasFloatingLabel || isFocused
                  ? placeholder
                  : undefined
              }
              value={value}
              $isError={!!error || hasError}
              $isActive={isActive}
              $hasLabel={hasLabel}
              $hasFloatingLabel={hasFloatingLabel}
              $isDisabled={extraProps.disabled}
              $size={size}
              onChange={onChange}
              onFocus={(e: any) => {
                setFocused(true)
                extraProps?.onFocus?.(e)
              }}
              onBlur={(e: any) => {
                extraProps?.onBlur?.(e)
                setFocused(false)
              }}
            />
            {rightAdornment}
          </SC.InputWrapper>
        </SC.InputContainer>
      </SC.Wrapper>
      {!error && helpText && <SC.HelpMessage>{helpText}</SC.HelpMessage>}
      {error && <SC.ErrorMessage>{error}</SC.ErrorMessage>}
    </SC.Container>
  )
}

// @ts-ignore
const FormFieldTextArea: FC<FormFieldTextAreaProps & { ref?: React.Ref<any> }> =
  forwardRef(FormFieldTextAreaRenderer)

export default FormFieldTextArea
